import React, { useEffect, useId, useState } from 'react';
import { ArrowDown, X } from 'lucide-react';
import { Reveal } from '../Reveal';
import { Eyebrow } from './SectionBand';
import { DeviceFrame } from './DeviceFrame';
import { proofAssets } from '../../lib/proofAssets';
const readerContext = ['Known reader interest', 'What matters to them', 'Where they are stuck', 'Actions they have taken', 'What kind of support may fit'];
const authorEnablement = ['Stronger sales conversations', 'Speaker Slides and CTAs', 'Workbook and Working Plans', 'Podcast Pitch and Prep', 'Email Follow-Up and Nurture', 'LinkedIn Posts and Paths', 'Clearer offer paths'];
const CONSOLE_SRC = proofAssets.authorConsole;
const CONSOLE_LABEL = 'Simplify to Amplify · Author console';
const CONSOLE_FRAME = 'Reader activity becomes author-side context you can actually use.';
function ConsoleLightbox({
  onClose
}: {
  onClose: () => void;
}) {
  const titleId = useId();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);
  return <div className="fixed inset-0 z-[70] flex items-center justify-center bg-navy/80 p-4 backdrop-blur-sm md:p-8" role="dialog" aria-modal="true" aria-labelledby={titleId} onClick={onClose} data-id="element-581" data-mp="146vc26nju65r">
      <div className="relative flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-paper shadow-[0_40px_80px_-20px_rgba(0,0,0,0.5)]" onClick={e => e.stopPropagation()} data-id="element-582" data-mp="ugsia48lso6d">
        <div className="sticky top-0 z-10 flex flex-shrink-0 items-start justify-between gap-4 border-b border-gray-200/80 bg-paper px-5 py-5 md:px-6 md:py-6" data-id="element-583" data-mp="li88xtoxnqut">
          <div className="min-w-0 max-w-3xl pr-2" data-id="element-584" data-mp="bizg545t0ia1">
            <p className="text-[0.7rem] font-semibold uppercase tracking-[0.2em] text-text-muted" data-id="element-585" data-mp="bkantf2dea6f">
              Start the next conversation with context
            </p>
            <p id={titleId} className="mt-2.5 font-serif text-xl leading-snug text-seaglass md:text-[1.65rem]" data-id="element-586" data-mp="6uztuhsi71dk">
              {CONSOLE_FRAME}
            </p>
          </div>
          <button type="button" onClick={onClose} className="flex-shrink-0 rounded-full p-2 text-text-muted transition-colors hover:bg-paper-dark hover:text-navy focus:outline-none focus:ring-2 focus:ring-seaglass" aria-label="Close preview" data-id="element-587" data-mp="3t1ol6egw940u">
            <X className="h-5 w-5" data-id="element-588" data-mp="pjwpaozmzzxd" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain bg-paper px-3 py-3 md:px-5 md:py-5" data-id="element-589" data-mp="btt3fz4o688g">
          <img src={CONSOLE_SRC} alt="Author console — reader activity becomes author-side context" className="mx-auto block h-auto w-full max-w-full rounded-lg" data-id="element-590" data-mp="fud2j88szlx8" />
        </div>
      </div>
    </div>;
}
export function AuthorLeverage() {
  const [enlarged, setEnlarged] = useState(false);
  return <section className="overflow-x-clip bg-navy px-6 py-20 text-paper md:px-10 md:py-28" data-id="element-591" data-mp="2t1cor5ewzrdb">
      <div className="mx-auto max-w-6xl" data-id="element-592" data-mp="2o31e4sgspcu1">
        <div className="max-w-3xl" data-id="element-593" data-mp="2o200qhcb4e95">
          <Reveal data-id="element-594" data-mp="2o0xbseyjolvv">
            <Eyebrow data-id="element-595" data-mp="32ree00j07ftt">Start the next conversation with context</Eyebrow>
            <h2 className="mt-4 font-serif text-3xl leading-[1.08] text-paper md:text-5xl" data-id="element-596" data-mp="32tqwcesobs7e">
              Know what mattered before the conversation starts.
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-paper/70" data-id="element-597" data-mp="2a3iiv0931vz5">
              The author can see the reader’s pattern, saved move, support
              interest, and activity—so follow-up is more relevant and the right
              offer is easier to identify.
            </p>
          </Reveal>
        </div>

        <div className="mt-14 grid min-w-0 items-start gap-10 lg:grid-cols-[1.2fr_1fr] lg:gap-14" data-id="element-598" data-mp="2f4693jphm8sb">
          <Reveal delay={0.1} className="min-w-0" data-id="element-599" data-mp="3vifwoblwnti3">
            <button type="button" onClick={() => setEnlarged(true)} className="group w-full max-w-full min-w-0 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-seaglass focus-visible:ring-offset-0" aria-label="Enlarge: Author console" data-id="element-600" data-mp="3qi1n2p4nobhy">
              <span className="block transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:shadow-[0_28px_50px_-28px_rgba(0,0,0,0.55)]" data-id="element-601" data-mp="2512kknoptzup">
                <DeviceFrame variant="browser" src={CONSOLE_SRC} alt="Author console — reader activity becomes author-side context" chromeLabel={CONSOLE_LABEL} aspect="16 / 10" data-id="element-602" data-mp="2e0pb2efwpl8z" />
              </span>
              <span className="mt-3 block text-center text-sm font-medium text-paper/55 transition-colors group-hover:text-paper/80" data-id="element-603" data-mp="2nwso4fcar4ap">
                Click to enlarge
              </span>
            </button>
          </Reveal>

          <Reveal delay={0.15} data-id="element-604" data-mp="2wsi4g9yw18h8">
            <div data-id="element-605" data-mp="31pqq4kj8vun8">
              <div className="rounded-2xl border border-white/12 bg-white/[0.04] p-6" data-id="element-606" data-mp="31q9yb9j3f4o2">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-seaglass-light" data-id="element-607" data-mp="36o20cwdgc75z">
                  Reader context
                </p>
                <div className="mt-3 flex flex-wrap gap-2" data-id="element-608" data-mp="3gqfdc54ok0nn">
                  {readerContext.map(item => <span key={item} className="rounded-lg border border-white/12 bg-navy-light/40 px-3 py-1.5 text-sm text-paper/85" data-id="element-609" data-mp="3bs43vviars5y">
                      {item}
                    </span>)}
                </div>
              </div>

              <div className="flex flex-col items-center py-3" data-id="element-610" data-mp="3dm1oaf1f6u9">
                <ArrowDown className="h-5 w-5 text-seaglass-light" data-id="element-611" data-mp="3cu6h0pcrb4i" />
                <span className="mt-1 text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-paper/45" data-id="element-612" data-mp="3uky9hyu167xi">
                  which becomes
                </span>
              </div>

              <div className="rounded-2xl border-2 border-seaglass/50 bg-white/[0.06] p-6 shadow-[0_24px_50px_-30px_rgba(0,0,0,0.6)]" data-id="element-613" data-mp="3ls4s9vv0wyh0">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-sand" data-id="element-614" data-mp="m5oc34135qw2">
                  Author enablement
                </p>
                <div className="mt-4 grid gap-x-6 gap-y-2.5 sm:grid-cols-2" data-id="element-615" data-mp="2blikerkthe4h">
                  {authorEnablement.map(item => <div key={item} className="flex items-start gap-2.5 text-paper/90" data-id="element-616" data-mp="2gkv4x5o7u93w">
                      <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sand" data-id="element-617" data-mp="21vp81ocuouz6" />
                      <span className="leading-snug" data-id="element-618" data-mp="26uimpk3vis0x">{item}</span>
                    </div>)}
                </div>
              </div>
            </div>
          </Reveal>
        </div>

        <p className="mx-auto mt-12 max-w-2xl text-center text-base text-paper/55" data-id="element-619" data-mp="1swu3egpgfsxz">
          <span className="block" data-id="element-620" data-mp="1xv5bon03pve3">
            Client experience shown to demonstrate the reader and author path.
          </span>
          <span className="mt-1 block" data-id="element-621" data-mp="1nvo94eg5bxmc">
            No performance outcomes are represented.
          </span>
        </p>
      </div>

      {enlarged && <ConsoleLightbox onClose={() => setEnlarged(false)} data-id="element-622" data-mp="3dbw1s6ae7qm5" />}
    </section>;
}